import { useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';

import { FileUpload, type FileUploadProps } from './FileUpload';
import {
  useGetPresignedUrl,
  useFileUploadToPresignedUrl,
  useMarkUploadCompleted,
} from '@/hooks/useFileUpload';
import type {
  PresignedUrlResponse,
  UploadCompletionResponse,
} from '@/types/file-upload';

interface ValidationRule {
  validate: (files: File[]) => string | null | Promise<string | null>;
  message: string;
}

interface PresignedFileUploadProps
  extends Omit<
    FileUploadProps,
    'onFileChange' | 'onChange' | 'isLoading' | 'value'
  > {
  value?: (PresignedUrlResponse | UploadCompletionResponse)[];
  onChange?: (
    value: (PresignedUrlResponse | UploadCompletionResponse)[],
  ) => void;
  name?: string;
  validationRules?: ValidationRule[];
}

const PresignedFileUpload = ({
  value = [],
  onChange,
  validationRules = [],
  className,
  acceptedTypes = '.zip,.pdf',
  multiple = false,
  disabled = false,
  ...fileUploadProps
}: PresignedFileUploadProps) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { getPresignedUrl } = useGetPresignedUrl();
  const { uploadToPresignedUrl } = useFileUploadToPresignedUrl();
  const { markUploadCompleted } = useMarkUploadCompleted();

  const validateFiles = useCallback(
    async (files: File[]): Promise<string | null> => {
      for (const rule of validationRules) {
        const error = await rule.validate(files);
        if (error) {
          return error;
        }
      }
      return null;
    },
    [validationRules],
  );

  const handleUploadSuccess = useCallback(
    (results: (PresignedUrlResponse | UploadCompletionResponse)[]) => {
      onChange?.(results);
    },
    [onChange],
  );

  const handleFileChange = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;

      setUploadError(null);

      const validationError = await validateFiles(files);
      if (validationError) {
        setUploadError(validationError);
        return;
      }

      try {
        setIsProcessing(true);
        const isSingleFile = files.length === 1;

        if (isSingleFile) {
          const file = files[0];

          try {
            const presignedResponse = await getPresignedUrl(file);
            await uploadToPresignedUrl(file, presignedResponse.signedUrl);
            const completionResponse = await markUploadCompleted(
              presignedResponse.id,
            );

            handleUploadSuccess([completionResponse]);
          } catch (error) {
            console.error('Upload error for file:', file.name, error);
            setUploadError('Upload failed. Please try again.');
            return;
          }
        } else {
          const uploadPromises = files.map(async (file) => {
            try {
              const presignedResponse = await getPresignedUrl(file);
              await uploadToPresignedUrl(file, presignedResponse.signedUrl);
              const completionResponse = await markUploadCompleted(
                presignedResponse.id,
              );

              return { success: true, result: completionResponse, file };
            } catch (error) {
              console.error('Upload error for file:', file.name, error);
              return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
                file,
              };
            }
          });

          const results = await Promise.all(uploadPromises);
          const successful = results.filter((r) => r.success);
          const failed = results.filter((r) => !r.success);

          if (failed.length === 0) {
            handleUploadSuccess(
              successful
                .map((r) => r.result)
                .filter(Boolean) as PresignedUrlResponse[],
            );
          } else if (successful.length === 0) {
            setUploadError('Upload failed. Please try again.');
          } else {
            setUploadError('Upload failed. Please try again.');

            if (successful.length > 0) {
              handleUploadSuccess(
                successful
                  .map((r) => r.result)
                  .filter(Boolean) as PresignedUrlResponse[],
              );
            }
          }
        }
      } catch (error) {
        console.error('Unexpected upload error:', error);
        setUploadError('Upload failed. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      validateFiles,
      getPresignedUrl,
      uploadToPresignedUrl,
      markUploadCompleted,
      handleUploadSuccess,
    ],
  );

  const handleFileSelect = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      const filesArray = Array.from(fileList);
      handleFileChange(filesArray);
    },
    [handleFileChange],
  );

  return (
    <div className="w-full space-y-4">
      <FileUpload
        {...fileUploadProps}
        className={className}
        acceptedTypes={acceptedTypes}
        multiple={multiple}
        disabled={disabled}
        isLoading={isProcessing}
        onFileChange={handleFileSelect}
      />

      {uploadError && (
        <div
          className="border-destructive/20 bg-destructive/5 flex items-start gap-3 rounded-lg border p-4"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-destructive text-sm font-medium">
              Upload Failed.
            </h3>
            <p className="text-destructive/80 mt-1 text-sm">{uploadError}</p>
          </div>
        </div>
      )}

      {process.env.NODE_ENV === 'development' && value && value.length > 0 && (
        <div className="text-muted-foreground text-xs">
          <strong>Current value:</strong> {value.length} file(s) uploaded
        </div>
      )}
    </div>
  );
};

export {
  PresignedFileUpload,
  type PresignedFileUploadProps,
  type ValidationRule,
};
